import {
  ThemeProvider,
  alpha,
  createTheme,
  darken,
  lighten,
} from "@mui/material/styles";
import CssBaseline from "@mui/material/CssBaseline";
import { useMemo } from "react";

export default function ThemeWrapper({ dominantColor, backgroundColor, children }) {
  const theme = useMemo(() => {
    const surfaceColor = lighten(backgroundColor, 0.18);
    const elevatedSurfaceColor = lighten(backgroundColor, 0.24);
    const accentSoft = alpha(dominantColor, 0.18);
    const accentStrong = alpha(dominantColor, 0.3);
    const outlineColor = alpha("#ffffff", 0.12);
    const textPrimary = "#f7f2fa";

    return createTheme({
      palette: {
        mode: "dark",
        primary: {
          main: dominantColor,
        },
        secondary: {
          main: lighten(dominantColor, 0.08),
        },
        background: {
          default: backgroundColor,
          paper: surfaceColor,
        },
        text: {
          primary: textPrimary,
          secondary: alpha(textPrimary, 0.72),
        },
        divider: outlineColor,
        surface: {
          main: surfaceColor,
          elevated: elevatedSurfaceColor,
        },
        outline: {
          main: outlineColor,
          accent: alpha(dominantColor, 0.28),
        },
        tonal: {
          soft: accentSoft,
          strong: accentStrong,
        },
      },
      shape: {
        borderRadius: 12,
      },
      typography: {
        fontFamily:
          '"Roboto","Noto Sans SC","Helvetica","Arial",sans-serif',
        button: {
          fontWeight: 700,
          letterSpacing: "0.02em",
          textTransform: "none",
        },
      },
      components: {
        MuiCssBaseline: {
          styleOverrides: {
            body: {
              backgroundColor,
              backgroundImage: `radial-gradient(circle at top, ${alpha(
                dominantColor,
                0.18
              )} 0%, transparent 32%), linear-gradient(180deg, ${lighten(
                backgroundColor,
                0.04
              )} 0%, ${backgroundColor} 100%)`,
            },
            "#root": {
              minHeight: "100vh",
            },
          },
        },
        MuiPaper: {
          styleOverrides: {
            root: {
              backgroundImage: "none",
              backdropFilter: "blur(18px)",
            },
            outlined: {
              borderColor: outlineColor,
            },
          },
        },
        MuiButton: {
          styleOverrides: {
            root: {
              borderRadius: 14,
              paddingInline: 18,
            },
            outlined: {
              borderColor: alpha(dominantColor, 0.32),
              "&:hover": {
                borderColor: dominantColor,
                backgroundColor: accentSoft,
              },
            },
            contained: {
              boxShadow: `0 12px 24px ${alpha(dominantColor, 0.24)}`,
              "&:hover": {
                boxShadow: `0 14px 28px ${alpha(dominantColor, 0.3)}`,
              },
            },
          },
        },
        MuiIconButton: {
          styleOverrides: {
            root: {
              border: `1px solid ${outlineColor}`,
            },
          },
        },
        MuiSlider: {
          styleOverrides: {
            root: {
              color: dominantColor,
            },
            thumb: {
              boxShadow: `0 0 0 6px ${alpha(dominantColor, 0.18)}`,
            },
            rail: {
              color: alpha("#ffffff", 0.18),
            },
            track: {
              border: "none",
            },
          },
        },
        MuiSwitch: {
          styleOverrides: {
            switchBase: {
              "&.Mui-checked": {
                color: dominantColor,
                "& + .MuiSwitch-track": {
                  backgroundColor: dominantColor,
                },
              },
            },
            track: {
              backgroundColor: alpha("#ffffff", 0.2),
            },
          },
        },
        MuiOutlinedInput: {
          styleOverrides: {
            root: {
              backgroundColor: alpha(elevatedSurfaceColor, 0.78),
              "& .MuiOutlinedInput-notchedOutline": {
                borderColor: outlineColor,
              },
              "&:hover .MuiOutlinedInput-notchedOutline": {
                borderColor: alpha(dominantColor, 0.35),
              },
              "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                borderColor: dominantColor,
              },
            },
          },
        },
        MuiInputLabel: {
          styleOverrides: {
            root: {
              color: alpha(textPrimary, 0.74),
            },
          },
        },
        MuiDialog: {
          styleOverrides: {
            paper: {
              backgroundColor: elevatedSurfaceColor,
              border: `1px solid ${outlineColor}`,
            },
          },
        },
        MuiPopover: {
          styleOverrides: {
            paper: {
              backgroundColor: darken(elevatedSurfaceColor, 0.03),
              border: `1px solid ${alpha(dominantColor, 0.18)}`,
            },
          },
        },
        MuiAlert: {
          styleOverrides: {
            filledSuccess: {
              backgroundColor: alpha(dominantColor, 0.88),
              color: textPrimary,
            },
          },
        },
      },
    });
  }, [backgroundColor, dominantColor]);

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      {children}
    </ThemeProvider>
  );
}
